import React, {useEffect, useRef} from "react";
import {
  Col,
  FormGroup,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import PropTypes from "prop-types";
import {useSelector} from "react-redux";
import {_Buttons} from "../../../constants/appConstants";
import {
  getCloneTargetMac,
  getCloneTaskId,
} from "../../../redux/Selectors/cloneProfile.selectors";
import {getSelectedController} from "../../../redux/Selectors/controllers.selectors";
import {useDisclosure} from "../../../hooks/useDisclosure";
import AsteriskIcon from "../../asteriskIcon";
import ZeusButton from "../../Button";
import SelectCreator from "../../Forms/components/SelectCreator";
import TreeSelector from "../../Forms/components/TreeSelector";
import Loader from "../../Loader";
import ConfirmModal from "../ConfirmModal";
import SensorCloningProgress from "./SensorCloningProgress";
import {useFormData} from "./useFormData";

const CloneProfile = (props) => {
  const {open, toggle} = props;
  const taskId = useSelector(getCloneTaskId);
  const targetMac = useSelector(getCloneTargetMac);
  const selectedController = useSelector(getSelectedController);

  // Polling/toasts/reset all run globally in CloneSensorsWatcher regardless
  // of this modal — a clone can keep running (and still report success or
  // failure via toast) after this modal is closed, the page is reloaded,
  // or the user navigates elsewhere. This modal only cares whether the
  // clone currently being tracked belongs to the controller it's open
  // for, to decide whether to show progress or the normal form.
  const isTrackingThisController = Boolean(
    taskId && selectedController?.mac && selectedController.mac === targetMac
  );

  // If this modal was showing progress and the watcher just cleared
  // taskId (clone finished, one way or another — the toast already fired
  // globally), close the modal automatically instead of leaving it open
  // on a form the user never asked for.
  const wasTrackingRef = useRef(isTrackingThisController);
  useEffect(() => {
    if (wasTrackingRef.current && !isTrackingThisController && open) {
      toggle();
    }
    wasTrackingRef.current = isTrackingThisController;
  }, [isTrackingThisController, open, toggle]);

  const {
    applianceOptions,
    customerOptions,
    handleChange,
    handleSubmit,
    payload,
    scopeOptions,
  } = useFormData();

  const {
    isOpen: isConfirmOpen,
    onClose: onConfirmClose,
    onOpen: onConfirmOpen,
  } = useDisclosure();

  const sourceApplianceLabel = applianceOptions.find(
    (option) => option.value === payload.appliance
  )?.label;

  const handleSaveClick = () => {
    if (!payload.appliance) {
      handleSubmit();
      return;
    }
    onConfirmOpen();
  };

  const handleConfirm = () => {
    handleSubmit();
    onConfirmClose();
  };

  return (
    <React.Fragment>
      <Modal
        unmountOnClose={true}
        isOpen={open}
        size={"md"}
        backdrop={isTrackingThisController ? "static" : true}
        keyboard={!isTrackingThisController}
      >
        <Loader />
        <ModalHeader>CLONE PROFILE</ModalHeader>
        <ModalBody>
          {isTrackingThisController ? (
            <SensorCloningProgress />
          ) : (
            <Row>
              <SelectCreator
                isRequired
                label="Customer"
                large
                onChange={(e) => handleChange("customer", e.target.value)}
                options={customerOptions}
                placeholder="Select Customer"
                value={payload.customer}
              />
              <Col xs="12">
                <FormGroup row>
                  <Col xs="12">
                    <div className="d-flex">
                      <label className="form-control-label roboto-bold" htmlFor="scope">
                        Scope
                      </label>
                      <AsteriskIcon />
                    </div>
                  </Col>
                  <Col xs="12">
                    <TreeSelector
                      childrenKey={"locations"}
                      defaultValue={payload.scope}
                      id={"scope"}
                      options={scopeOptions}
                      onChange={(value) => handleChange("scope", value)}
                      placeholder="Select Scope"
                    />
                  </Col>
                </FormGroup>
              </Col>
              <SelectCreator
                isRequired
                label="Appliance"
                large
                onChange={(e) => handleChange("appliance", e.target.value)}
                options={applianceOptions}
                placeholder="Select Appliance"
                value={payload.appliance}
              />
            </Row>
          )}
        </ModalBody>
        {!isTrackingThisController && (
          <ModalFooter>
            <div className="mt-4 mt-lg-0 d-flex justify-content-end">
              <ZeusButton data-cy="submit-btn" onClick={handleSaveClick}>
                {_Buttons.SAVE}
              </ZeusButton>
              <ZeusButton data-cy="cancel-btn" onClick={toggle} type={"secondary"}>
                {_Buttons.CANCEL}
              </ZeusButton>
            </div>
          </ModalFooter>
        )}
      </Modal>
      {isConfirmOpen && (
        <ConfirmModal
          header="Clone Profile"
          modalBody={`Are you sure you want to clone the profile from "${sourceApplianceLabel}"?`}
          onClose={onConfirmClose}
          onConfirm={handleConfirm}
          open={isConfirmOpen}
        />
      )}
    </React.Fragment>
  );
};

CloneProfile.propTypes = {
  open: PropTypes.bool,
  toggle: PropTypes.func,
};

export default CloneProfile;
